import { PencilIcon, ShieldIcon } from 'lucide-react';
import { formatUsd } from 'shared';
import { cartDueToday, cartGrandTotal, cartLines, cartMonthlyTotal } from '@/state/pricing.js';
import { paymentTermsLabel, tierSpecLabels } from '@/lib/formatters.js';
import { selectedSurchargeDetails } from '@/lib/surcharges.js';
import { openPaymentFor } from '@/flows/marketplace/state/step.js';

interface OrderSummaryProps {
  /** Off during the loop; on at checkout, where this is the review. */
  editable?: boolean;
}

export function OrderSummary({ editable = false }: OrderSummaryProps) {
  const lines = cartLines.value;
  const grand = cartGrandTotal.value;
  const monthly = cartMonthlyTotal.value;

  return (
    <aside className="sm-panel sm-stack sm-card">
      <p className="sm-type-eyebrow">Order summary</p>

      {lines.length === 0 ? (
        <div className="sm-stack sm-order-summary-empty">
          <span className="sm-center sm-empty-disc sm-order-summary-empty-icon">
            <ShieldIcon size={16} aria-hidden="true" />
          </span>
          <div className="sm-stack sm-pair">
            {/* Neutral rather than "nothing added yet" — a re-rate can empty a summary
                the customer had already filled. */}
            <p className="sm-type-body-strong">No plans selected</p>
            <p className="sm-type-caption">Add a plan to see your total here.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="sm-stack sm-summary-list">
            {lines.map((line) => {
              const { tier, entry } = line;
              const { termMonthsLabel, coverageMilesLabel, deductibleLabel } = tierSpecLabels(tier);
              // A null deductible formats as an em dash, which reads as nonsense next to
              // the "deductible" suffix — drop the segment instead.
              const meta = [
                termMonthsLabel,
                coverageMilesLabel,
                tier.deductible_amount === null ? null : `${deductibleLabel} deductible`,
              ]
                .filter(Boolean)
                .join(' · ');
              const addons = selectedSurchargeDetails(tier, entry.surcharges);
              return (
                <div className="sm-stack sm-summary-item" key={entry.quoteItemId}>
                  <div className="sm-summary-item-head">
                    <div className="sm-stack sm-pair">
                      <p className="sm-type-body-strong">{tier.product_name}</p>
                      <p className="sm-type-caption">{meta}</p>
                    </div>
                    <p className="sm-type-body-strong sm-tabular sm-summary-item-price">
                      {formatUsd(line.salePrice)}
                    </p>
                  </div>
                  {addons.length > 0 && (
                    <ul className="sm-stack sm-summary-addons">
                      {addons.map((a) => (
                        <li className="sm-summary-addon" key={a.code}>
                          <span className="sm-type-caption sm-summary-addon-name">
                            {a.description}
                            {!a.optional && (
                              <span className="sm-type-eyebrow sm-addon-tag">Included</span>
                            )}
                          </span>
                          {a.optional && (
                            <span className="sm-type-caption sm-tabular sm-summary-addon-price">
                              +{formatUsd(a.basePrice)}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="sm-summary-item-terms">
                    <span className="sm-type-caption">{paymentTermsLabel(line)}</span>
                    {editable && (
                      <button
                        type="button"
                        className="sm-type-caption sm-link sm-summary-item-edit"
                        onClick={() => openPaymentFor(line.index, 'customer-info')}
                      >
                        <PencilIcon size={12} aria-hidden="true" />
                        Edit
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="sm-stack sm-totals">
            <div className="sm-type-body sm-tabular sm-totals-row">
              <span>Tax</span>
              <span>{formatUsd(grand.taxes)}</span>
            </div>
            <div className="sm-type-body sm-tabular sm-totals-row">
              <span>Order total</span>
              <span>{formatUsd(grand.total)}</span>
            </div>
            {monthly !== null && (
              <div className="sm-type-body sm-tabular sm-totals-row">
                <span>Then monthly</span>
                <span>{formatUsd(monthly)}/mo</span>
              </div>
            )}
          </div>

          <div className="sm-stack sm-totals sm-totals-due">
            <div className="sm-type-heading sm-tabular sm-totals-row" data-tone="total">
              <span>Due today</span>
              <span>{formatUsd(cartDueToday.value)}</span>
            </div>
          </div>
        </>
      )}
    </aside>
  );
}
